// src/pages/TransactionEntry/TransactionEntryPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import useTransactionRows from "./useTransactionRows";
import TransactionRow from "./TransactionRow";
import { saveAllTransactions } from "./transactionEntrySave";
import { parseTransactionExcel } from "./transactionExcelImport";
import "./transactionEntry.css";
import { getFunctions, httpsCallable } from "firebase/functions";

function computeDashboardSnapshot(accounts) {
  const val = (name) => {
    const item = accounts.find((a) =>
      (a.name || "").toLowerCase().includes(name.toLowerCase().trim())
    );
    return Number(item?.currentBalance || 0);
  };

  const comfortTotal = val("cash in hand - comfort line");
  const visa = val("visa");
  const tanzeelCash = comfortTotal - visa;

  const loliaCash = val("cash in hand - lolia");

  const noqodiCL = val("noqodi wallet - comfort");
  const noqodiLolia = val("noqodi wallet - lolia");

  const netCash = tanzeelCash + loliaCash;

  return { comfortTotal, visa, tanzeelCash, noqodiCL, loliaCash, noqodiLolia, netCash };
}


export default function TransactionEntryPage() {
  const {
    rows,
    addRow,
    updateRow,
    removeRow,
    setRowError,
    replaceRows,
    resetRows,
  } = useTransactionRows();

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [excelImportInfo, setExcelImportInfo] = useState(null);
  const excelInputRef = useRef(null);

  // A row counts as unsaved only when the user entered meaningful transaction data.
  // The default date alone does not trigger the warning.
  const hasUnsavedTransactions = useMemo(
    () =>
      rows.some((row) => {
        const amountIn = Number(row.amountIn || 0);
        const amountOut = Number(row.amountOut || 0);

        return Boolean(
          row.accountId ||
            row.expenseType ||
            row.description ||
            amountIn !== 0 ||
            amountOut !== 0 ||
            row.paidBy ||
            row.comment ||
            row.proofFile
        );
      }),
    [rows]
  );

  // Protect browser refresh / tab close / browser close while entries are unsaved
  // or while a bulk save is still running. Browsers show their own standard warning text.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedTransactions && !saving && !importingExcel) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedTransactions, saving, importingExcel]);

  // React Admin sidebar items render as links. Capture link clicks before routing so
  // an accidental click on Dashboard / Employees / Ledger / another menu does not
  // destroy unsaved transaction rows. Nothing is stored or posted by this guard.
  useEffect(() => {
    const handleNavigationClick = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) return;

      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href") || "";
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;

      let targetUrl;
      try {
        targetUrl = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      // Do not warn for a click that leaves the user on this exact URL.
      if (targetUrl.href === window.location.href) return;

      if (saving || importingExcel) {
        event.preventDefault();
        event.stopPropagation();
        window.alert(
          saving
            ? "Transactions are currently saving. Please wait until saving is complete."
            : "The Excel file is currently being loaded. Please wait until the import finishes."
        );
        return;
      }

      if (!hasUnsavedTransactions) return;

      const leave = window.confirm(
        "You have unsaved transactions.\n\nLeaving this page will discard all entries that have not been saved.\n\nPress OK to leave without saving, or Cancel to stay on this page."
      );

      if (!leave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", handleNavigationClick, true);
    return () => document.removeEventListener("click", handleNavigationClick, true);
  }, [hasUnsavedTransactions, saving, importingExcel]);

  // Load accounts once
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const snap = await getDocs(collection(db, "accounts"));
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));
        // Sort by name for easier selection
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setAccounts(list);
      } catch (err) {
        console.error("Error loading accounts:", err);
      } finally {
        setLoadingAccounts(false);
      }
    };
    loadAccounts();
  }, []);

  const totals = useMemo(() => {
    let inTotal = 0;
    let outTotal = 0;
    rows.forEach((r) => {
      inTotal += Number(r.amountIn || 0);
      outTotal += Number(r.amountOut || 0);
    });
    return { inTotal, outTotal };
  }, [rows]);

  const handleFileChange = (id, file) => {
    updateRow(id, "proofFile", file);
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files?.[0];
    // Allow selecting the same file again after a failed/aborted import.
    event.target.value = "";

    if (!file || importingExcel || saving) return;

    if (hasUnsavedTransactions) {
      const replaceExisting = window.confirm(
        "You already have unsaved transaction rows on this page.\n\nImporting Excel will replace those unsaved rows.\n\nPress OK to continue, or Cancel to keep the current rows."
      );
      if (!replaceExisting) return;
    }

    setImportingExcel(true);
    setExcelImportInfo(null);

    try {
      const result = await parseTransactionExcel(file, accounts);

      if (result.errors.length > 0) {
        const preview = result.errors.slice(0, 15).join("\n");
        const more =
          result.errors.length > 15
            ? `\n...and ${result.errors.length - 15} more error(s).`
            : "";

        window.alert(
          `Excel import stopped. Nothing was loaded.\n\n${preview}${more}`
        );
        return;
      }

      if (!result.rows.length) {
        window.alert("No transaction rows were found in the Excel file.");
        return;
      }

      replaceRows(result.rows);
      setExcelImportInfo({
        fileName: file.name,
        rowCount: result.rows.length,
        totalIn: result.totalIn,
        totalOut: result.totalOut,
      });

      window.alert(
        `Excel loaded into Transaction Entry.\n\nRows: ${result.rows.length}\nAmount In: AED ${result.totalIn.toFixed(2)}\nAmount Out: AED ${result.totalOut.toFixed(2)}\n\nPlease review the rows, then press Save All Transactions.`
      );
    } catch (err) {
      console.error("Excel transaction import failed:", err);
      window.alert(err?.message || "Failed to read the Excel file.");
    } finally {
      setImportingExcel(false);
    }
  };

  const handleClearAll = () => {
    if (saving) return;

    if (hasUnsavedTransactions) {
      const clear = window.confirm(
        "Clear all unsaved transaction rows?\n\nThis cannot be undone."
      );
      if (!clear) return;
    }

    resetRows();
    setExcelImportInfo(null);
  };

  const handleSaveAll = async () => {
  if (saving) return;

  setSaving(true);
  try {
    // Snapshot BEFORE (fresh fetch)
    const beforeSnap = await getDocs(collection(db, "accounts"));
    const beforeAccounts = beforeSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
    const beforeSummary = computeDashboardSnapshot(beforeAccounts);

    // Save all using existing logic
    const result = await saveAllTransactions(rows);

    if (!result.ok) {
      if (result.rowErrors) {
        Object.entries(result.rowErrors).forEach(([rowId, msg]) => {
          setRowError(Number(rowId), msg);
        });
        return;
      }

      if (result.failedRows?.length) {
        // Some rows may already have been posted successfully. Remove those from
        // the entry screen so pressing Save again cannot post them a second time.
        // Only the rows that truly failed remain available for correction/retry.
        const failedById = new Map(
          result.failedRows.map((item) => [item.id, item.error])
        );
        const retryRows = rows
          .filter((row) => failedById.has(row.id))
          .map((row) => ({
            ...row,
            error: failedById.get(row.id) || "Transaction save failed.",
          }));

        replaceRows(retryRows);
        setExcelImportInfo(null);
      }
      return;
    }

    // Snapshot AFTER (fresh fetch)
    const afterSnap = await getDocs(collection(db, "accounts"));
    const afterAccounts = afterSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
    const afterSummary = computeDashboardSnapshot(afterAccounts);

    // Map accountId -> accountName (so email looks like ledger)
    const accountNameById = new Map(afterAccounts.map((a) => [a.id, a.name || ""]));

    // Prepare ledger rows from UI (only non-empty rows)
    const ledgerRows = rows
      .filter((r) => {
        const inAmt = Number(r.amountIn || 0);
        const outAmt = Number(r.amountOut || 0);
        return r.accountId || r.description || r.expenseType || inAmt !== 0 || outAmt !== 0;
      })
      .map((r) => ({
        accountId: r.accountId,
        accountName: accountNameById.get(r.accountId) || "",
        date: r.date,
        expenseType: r.expenseType || "",
        description: r.description || "",
        amountIn: Number(r.amountIn || 0),
        amountOut: Number(r.amountOut || 0),
        paidBy: r.paidBy || "",
        comment: r.comment || "",
      }));

    // Only email when entries are expenses/receiving (in/out)
    const shouldEmail = ledgerRows.some(
      (r) => Number(r.amountOut) > 0 || Number(r.amountIn) > 0
    );

    if (shouldEmail) {
      const fn = httpsCallable(getFunctions(), "sendTxBatchEmail");
      const createdByEmail = localStorage.getItem("email") || "";
      await fn({ createdByEmail, ledgerRows, beforeSummary, afterSummary });
    }

    // Clear form only after the full batch succeeds.
    resetRows();
    setExcelImportInfo(null);
  } catch (err) {
    console.error("Bulk save error:", err);
    window.alert(err.message || "Error while saving transactions.");
  } finally {
    setSaving(false);
  }
};


  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 8 }}>Transaction Entry (Multi-Row)</h2>
      <div style={{ fontSize: 12, marginBottom: 12, color: "#555" }}>
        Enter multiple transactions in one screen. All entries use your existing
        logic for balances & mirror transactions. Excel import only fills this same
        entry table; it does not post anything until Save All Transactions is pressed.
        Excel columns: Account Head, Date, Type, Description, Amount In, Amount Out,
        Paid By, Comment.
      </div>

      {hasUnsavedTransactions && !saving && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 10px",
            border: "1px solid #f59e0b",
            borderRadius: 6,
            background: "#fffbeb",
            color: "#92400e",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⚠ Unsaved transactions on this page. Save them before opening another dashboard menu.
        </div>
      )}

      {saving && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 10px",
            border: "1px solid #93c5fd",
            borderRadius: 6,
            background: "#eff6ff",
            color: "#1e40af",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Saving transactions… please do not leave this page until saving finishes.
        </div>
      )}

      {loadingAccounts ? (
        <div style={{ padding: 10, fontSize: 13 }}>Loading accounts...</div>
      ) : accounts.length === 0 ? (
        <div style={{ padding: 10, fontSize: 13, color: "#b71c1c" }}>
          ⚠ No accounts found. Please create account heads first.
        </div>
      ) : (
        <>
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelImport}
            style={{ display: "none" }}
          />

          {excelImportInfo && (
            <div
              style={{
                marginBottom: 12,
                padding: "9px 11px",
                border: "1px solid #86efac",
                borderRadius: 6,
                background: "#f0fdf4",
                color: "#166534",
                fontSize: 12,
              }}
            >
              <strong>Excel loaded for review:</strong> {excelImportInfo.fileName}
              {" · "}
              {excelImportInfo.rowCount} row(s)
              {" · Amount In: AED "}
              {excelImportInfo.totalIn.toFixed(2)}
              {" · Amount Out: AED "}
              {excelImportInfo.totalOut.toFixed(2)}
              <div style={{ marginTop: 4 }}>
                Nothing has been posted yet. Review the rows and press Save All Transactions when ready.
              </div>
            </div>
          )}

          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Account Head</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Amount In</th>
                  <th style={thStyle}>Amount Out</th>
                  <th style={thStyle}>Paid By</th>
                  <th style={thStyle}>Comment</th>
                  <th style={thStyle}>Proof</th>
                  <th style={thStyle}>Remove</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row, idx) => (
                  <TransactionRow
                    key={row.id}
                    row={row}
                    index={idx}
                    accounts={accounts}
                    onChange={updateRow}
                    onFileChange={handleFileChange}
                    onRemove={rows.length > 1 ? removeRow : () => {}}
                  />
                ))}
              </tbody>

              {/* Totals row */}
              <tfoot>
                <tr style={{ background: "#e3f2fd" }}>
                  <td
                    colSpan={5}
                    style={{
                      borderTop: "1px solid #90caf9",
                      padding: "4px 6px",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  >
                    Totals:
                  </td>
                  <td
                    style={{
                      borderTop: "1px solid #90caf9",
                      padding: "4px 6px",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  >
                    {totals.inTotal.toFixed(2)}
                  </td>
                  <td
                    style={{
                      borderTop: "1px solid #90caf9",
                      padding: "4px 6px",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  >
                    {totals.outTotal.toFixed(2)}
                  </td>
                  <td colSpan={4} style={{ borderTop: "1px solid #90caf9" }} />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Actions */}
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={addRow}
              style={btnSecondary}
              disabled={saving || importingExcel}
            >
              ➕ Add Row
            </button>

            <button
              type="button"
              onClick={() => excelInputRef.current?.click()}
              style={btnSecondary}
              disabled={saving || importingExcel || accounts.length === 0}
            >
              {importingExcel ? "Reading Excel..." : "📥 Import Excel"}
            </button>

            <button
              type="button"
              onClick={handleSaveAll}
              style={btnPrimary}
              disabled={saving || importingExcel || accounts.length === 0}
            >
              {saving ? "Saving..." : "💾 Save All Transactions"}
            </button>

            <button
              type="button"
              onClick={handleClearAll}
              style={btnGhost}
              disabled={saving || importingExcel}
            >
              Clear All
            </button>

            <div style={{ fontSize: 11, color: "#666" }}>
              Tip: Amount In / Out will reflect in your existing ledger and
              mirror rules automatically.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = {
  border: "1px solid #ddd",
  padding: "4px 6px",
  fontSize: 11,
  fontWeight: 600,
  textAlign: "center",
  whiteSpace: "nowrap",
};

const btnPrimary = {
  background: "#1976d2",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12,
  cursor: "pointer",
};

const btnSecondary = {
  background: "#fff",
  color: "#1976d2",
  border: "1px solid #1976d2",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12,
  cursor: "pointer",
};

const btnGhost = {
  background: "#fff",
  color: "#555",
  border: "1px solid #ccc",
  borderRadius: 4,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};
